import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useSessionStore, waitForHydration } from '@/stores/sessionStore'
import { usePOSStore } from '@/stores/posStore'
import { v2api, v2Unscoped } from '@/api/v2'
import type { Store, Register } from '@/types'
import { Leaf, AlertCircle } from 'lucide-react'

type SearchParams = {
  access: string
  refresh: string
  store_id: number
  pos_id: number
  backend_url: string
}

export const Route = createFileRoute('/windows-login')({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    access: typeof search.access === 'string' ? search.access : '',
    refresh: typeof search.refresh === 'string' ? search.refresh : '',
    store_id: Number(search.store_id) || 0,
    pos_id: Number(search.pos_id) || 0,
    backend_url: typeof search.backend_url === 'string' ? search.backend_url : '',
  }),
  component: WindowsLoginPage,
})

interface RawRegister {
  id: number
  name: string
  store?: number
  store_id?: number
  terminal_id?: number | string
  is_safe?: boolean
  local_printing_enabled?: boolean
}

function WindowsLoginPage() {
  const navigate = useNavigate()
  const { access, refresh, store_id, pos_id, backend_url } = Route.useSearch()
  const [error, setError] = useState<string | null>(null)
  const ranRef = useRef(false)

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    async function run() {
      if (!access || !refresh || !store_id || !pos_id || !backend_url) {
        setError('Missing required parameters: access, refresh, store_id, pos_id, backend_url')
        return
      }

      try {
        await waitForHydration()

        const session = useSessionStore.getState()
        const { clearCart } = usePOSStore.getState()

        // Drop any prior clerk session — the user must re-enter their PIN.
        session.clearClerkAuth()

        const normalizedBackendUrl = backend_url.replace(/\/$/, '')
        session.setBackendUrl(normalizedBackendUrl)

        // Seed a placeholder store so `setAuth` can store JWT + auth_type.
        // Mirrors what useAuthLogin does — real fields get backfilled below.
        const placeholderStore: Store = {
          id: store_id,
          name: `Store ${store_id}`,
          address: null,
          city: null,
          province: null,
          postal_code: null,
          phone: null,
          timezone: 'America/Toronto',
          is_cannabis: true,
          default_tax_rate: 13,
        }
        session.setAuth(access, refresh, 'store', placeholderStore)

        // Backfill real store details (mirrors useBackfillSelectedStore).
        // The `_` cache-buster forces a unique URL so the browser cache is
        // bypassed. The Electron shell (waio-windows-v2) otherwise revalidates
        // this GET with If-None-Match, the Tornado origin returns 304 (it
        // auto-computes ETags), and the browser re-serves a stored copy whose
        // bytes fail to decode (Cloudflare/origin content-encoding mismatch),
        // surfacing as `net::ERR_CONTENT_DECODING_FAILED` → axios "Network
        // Error" → "Sign-in failed". A unique URL has no cache entry, so it
        // always fetches a fresh 200 and can never hit the poisoned 304 path.
        const storeData = await v2api.get<
          Record<string, unknown> | { store: Record<string, unknown> }
        >(v2Unscoped(`/objects/stores/${store_id}`), { _: Date.now() })
        const storeRaw =
          storeData && typeof storeData === 'object' && 'store' in storeData
            ? (storeData as { store: Record<string, unknown> }).store
            : (storeData as Record<string, unknown>)

        const realName =
          typeof storeRaw.name === 'string' && storeRaw.name.trim()
            ? storeRaw.name.trim()
            : placeholderStore.name
        const realStore: Store = {
          ...placeholderStore,
          name: realName,
          address: typeof storeRaw.address === 'string' ? storeRaw.address : null,
          city: typeof storeRaw.city === 'string' ? storeRaw.city : null,
          province: typeof storeRaw.province === 'string' ? storeRaw.province : null,
          postal_code: typeof storeRaw.postal_code === 'string' ? storeRaw.postal_code : null,
          phone: typeof storeRaw.phone === 'string' ? storeRaw.phone : null,
        }
        useSessionStore.getState().setStore(realStore)

        // Fetch registers and pick the one matching pos_id (mirrors useRegisters).
        // Cache-busted for the same reason as the store fetch above — this is
        // the GET that was 304-ing to ERR_CONTENT_DECODING_FAILED in the
        // Electron shell.
        const rawRegisters = await v2api.get<RawRegister[]>(
          v2Unscoped('/objects/pos_registers'),
          { _: Date.now() },
        )
        const match = (Array.isArray(rawRegisters) ? rawRegisters : []).find(
          (r) =>
            Number(r.id) === pos_id &&
            Number(r.store ?? r.store_id) === store_id &&
            !r.is_safe,
        )

        if (!match) {
          setError(`Register ${pos_id} not found for store ${store_id}`)
          return
        }

        const register: Register = {
          id: match.id,
          name: match.name,
          terminal_id: String(match.terminal_id || match.id),
          store_id: Number(match.store ?? match.store_id),
          store_name: realStore.name,
          is_active: true,
          last_activity: null,
          local_printing_enabled: !!match.local_printing_enabled,
        }

        useSessionStore.getState().setRegister(register, clearCart)

        navigate({ to: '/login', replace: true })
      } catch (err) {
        console.error('[WindowsLogin] Failed:', err)
        setError(err instanceof Error ? err.message : 'Failed to log in via Windows app')
      }
    }

    run()
  }, [access, refresh, store_id, pos_id, backend_url, navigate])

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-600 to-green-800 p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur rounded-2xl mb-4">
            <Leaf className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">WAIO POS</h1>
          <p className="text-green-100 mt-2">
            {error ? 'Sign-in failed' : 'Signing you in…'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6">
          {error ? (
            <div className="flex flex-col items-center text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-3 py-6">
              <div className="w-6 h-6 border-2 border-green-200 border-t-green-500 rounded-full animate-spin" />
              <p className="text-gray-600">Connecting…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
